/**
 * SidebarService
 *
 * @module client/src/app/shared/services
 */
import { Injectable } from '@angular/core';
import { Observable } from '@shared/rx';
import { map, compact, identity, isEmpty, omit } from 'lodash';
import { IMenuItem, PermissionValue } from '@shared/types';
import * as routes from '@app/routes';
import { HAWKER_SESSION_ADMIN_MODULE } from '@app/routes';
import { select } from '@angular-redux/store';

import { StateService, STATE_SELECTORS } from '../state-service';
import {
  getCustomEntityName,
  getPluralEntityName,
  getSingularEntityName,
} from '@shared/configs';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';
import { I18n } from '@app/i18n/strings-keys';
import { ROUTES_PERMISSIONS } from '@app/routes-permissions';
import { PermissionService } from '../session/permission.service';

export const MENU_ITEMS: IMenuItem[] = [
  {
    id: 'Inventory',
    title: I18n.Application.Inventory,
    subItemsArray: [
      {
        title: getSingularEntityName(ENTITY_TYPES.DASHBOARD),
        route: routes.DASHBOARD_ROUTE,
      },
      {
        title: getPluralEntityName(ENTITY_TYPES.TRANSFER),
        subItemsArray: [
          {
            title: getCustomEntityName(ENTITY_TYPES.TRANSFER_GROUP),
            route: routes.TRANSFER_GROUP_MODULE,
            requiredPermissions: ['transfer.View'],
          },
          {
            title: getCustomEntityName(ENTITY_TYPES.TRANSFER_GROUP_TEMPLATE),
            route: routes.TRANSFER_GROUP_TEMPLATE_MODULE,
          },
          {
            title: I18n.Routes.TransferListTitle,
            route: routes.TRANSFER_MODULE,
          },
          {
            title: getPluralEntityName(ENTITY_TYPES.INCOMPLETE_TRANSFER),
            route: routes.INCOMPLETE_TRANSFER_MODULE,
            requiredPermissions: ['transfer.View'],
          },
          {
            title: getPluralEntityName(ENTITY_TYPES.HISTORICAL_QUANTITIES),
            route: routes.HISTORICAL_TRANSFER_QUANTITIES_MODULE,
            requiredPermissions: ['transfer.View'],
          },
          {
            title: getPluralEntityName(ENTITY_TYPES.TRANSFER_REQUEST),
            route: routes.TRANSFER_REQUEST_MODULE,
            requiredPermissions: ['transfer.View'],
          },
        ],
        requiredPermissions: ['transfer.View'],
      },
      {
        title: getPluralEntityName(ENTITY_TYPES.ORDER),
        route: routes.ORDER_MODULE,
      },
      {
        title: getPluralEntityName(ENTITY_TYPES.INVENTORY_COUNT),
        route: routes.INVENTORY_COUNT_GROUP_MODULE,
        requiredPermissions: ['inventory-count.View'],
      },
      {
        title: getPluralEntityName(ENTITY_TYPES.STAND_WORKSHEET),
        route: routes.STAND_WORKSHEET_MODULE,
      },
      {
        title: I18n.EntityNames.Hawkers,
        subItemsArray: [
          {
            title: I18n.Routes.HawkerProfileListTitle,
            route: routes.HAWKER_PROFILE_MODULE,
            requiredPermissions:
              ROUTES_PERMISSIONS[routes.HAWKER_PROFILE_MODULE],
          },
          {
            title: I18n.EntityNames.HawkerLoad,
            route: routes.HAWKER_LOAD_MODULE,
            requiredPermissions:
              ROUTES_PERMISSIONS[routes.HAWKER_PROFILE_MODULE],
          },
          {
            title: I18n.EntityNames.HawkerCommissaryAssignment,
            route: routes.HAWKER_COMMISSARY_ASSIGNMENT_MODULE,
            requiredPermissions:
              ROUTES_PERMISSIONS[routes.HAWKER_PROFILE_MODULE],
          },
          {
            title: I18n.EntityNames.HawkerCommissaryAssignmentHistory,
            route: routes.HAWKER_COMMISSARY_ASSIGNMENT_HISTORY_MODULE,
            requiredPermissions:
              ROUTES_PERMISSIONS[
                routes.HAWKER_COMMISSARY_ASSIGNMENT_HISTORY_MODULE
              ],
          },
          {
            title: I18n.EntityNames.HawkerSession,
            route: routes.HAWKER_SESSION_MODULE,
            requiredPermissions: [
              `${ENTITY_TYPES.HAWKER_SESSION}.${PermissionValue.View}`,
              (permissions) => {
                const hasEditPermission = PermissionService.hasPermissions(
                  permissions,
                  `${ENTITY_TYPES.HAWKER_CONFIG}.${PermissionValue.Edit}`,
                );
                const hasViewPermission = PermissionService.hasPermissions(
                  permissions,
                  `${ENTITY_TYPES.HAWKER_CONFIG}.${PermissionValue.View}`,
                );

                return {
                  name: `${ENTITY_TYPES.HAWKER_SESSION}.${PermissionValue.View}`,
                  hasPermission: !(hasEditPermission && hasViewPermission),
                };
              },
            ],
          },
          {
            title: I18n.EntityNames.HawkerSession,
            route: HAWKER_SESSION_ADMIN_MODULE,
            requiredPermissions:
              ROUTES_PERMISSIONS[routes.HAWKER_SESSION_ADMIN_MODULE],
          },
        ],
      },
    ],
  },
];

/**
 * # SidebarService
 * Defines the service for sidebar data providing
 */
@Injectable()
export class SidebarService {
  @select(['ui', 'menuItems']) public items: Observable<IMenuItem[]>;
  @select(['ui', 'selectedMenuItem'])
  public selectedItem: Observable<IMenuItem>;

  /**
   * createItemsCopy
   * creates a deep copy of MenuItems applying an optional mapper.
   * - All items for which mapper returns falsy value will be removed.
   * - An empty subItemsArray will be removed.
   * - A mapper function will be called on node with already mapped subItems.
   */
  public static createItemsCopy(
    items: IMenuItem[],
    mapper: (menuItem: IMenuItem) => IMenuItem = identity,
  ): IMenuItem[] {
    return compact(
      map(items, (item) => {
        const subItemsArray = SidebarService.createItemsCopy(
          item.subItemsArray,
          mapper,
        );
        const subItemsProperty = isEmpty(subItemsArray)
          ? null
          : { subItemsArray };

        return mapper(
          Object.assign(
            {},
            omit(item, 'subItemsArray'),
            subItemsProperty,
          ) as IMenuItem,
        );
      }),
    );
  }

  public constructor(private stateService: StateService) {}

  /**
   * This function creates list of menu items
   * Use subItemsArray to create sub-menu:  { title: 'Sub Menu Items',  subItemsArray: [] }
   *
   * @returns {IMenuItem[]}
   */

  public createItems(): IMenuItem[] {
    const omitHidden = (item: IMenuItem): IMenuItem =>
      item.hidden ? null : item;

    return SidebarService.createItemsCopy(MENU_ITEMS, omitHidden);
  }

  public getCurrentItems(): IMenuItem[] {
    return this.stateService.getBySelector(STATE_SELECTORS.SIDEBAR_ITEMS);
  }

  /**
   * mapCurrentItems
   * creates a deep copy of the current sidebar items and applies an optional mapper to every item.
   * - All items for which mapper returns falsy value will be removed.
   * - An empty subItemsArray will be removed.
   * - A mapper function will be called on node with already mapped subItems.
   */
  public mapCurrentItems(
    mapper: (item: IMenuItem) => IMenuItem = identity,
  ): IMenuItem[] {
    return SidebarService.createItemsCopy(this.getCurrentItems(), mapper);
  }

  /**
   * Transforms the current items tree into a flat array
   */
  public getCurrentItemsFlattened(): IMenuItem[] {
    const flatItems: IMenuItem[] = [];
    this.mapCurrentItems((item) => (flatItems.push(item), item));

    return flatItems;
  }
}
